// src/utils/optimisation.js

/**
 * Algorithme d'optimisation des découpes de plaques de placoplâtre
 * Amélioré avec pose en quinconce, réutilisation active des chutes et optimisation de l'orientation
 * Système de coordonnées avec origine en bas à gauche
 */

// Seuil minimal pour considérer une chute comme réutilisable (en cm²)
// Exemple: 20cm x 20cm = 400cm²
const SURFACE_MINIMALE_REUTILISABLE = 400;

// --- Fonctions Utilitaires Géométriques ---

// Vérifie si deux rectangles se chevauchent
const rectanglesSeChevaucent = (rect1, rect2) => {
  return !(
    rect1.x + rect1.largeur <= rect2.x ||
    rect2.x + rect2.largeur <= rect1.x ||
    rect1.y + rect1.hauteur <= rect2.y ||
    rect2.y + rect2.hauteur <= rect1.y
  );
};

// Vérifie si un rectangle contient entièrement un autre rectangle
const rectangleContient = (rectExterieur, rectInterieur) => {
  return (
    rectInterieur.x >= rectExterieur.x &&
    rectInterieur.y >= rectExterieur.y &&
    rectInterieur.x + rectInterieur.largeur <= rectExterieur.x + rectExterieur.largeur &&
    rectInterieur.y + rectInterieur.hauteur <= rectExterieur.y + rectExterieur.hauteur
  );
};

// Calcule l'intersection entre deux rectangles
const calculerIntersection = (rect1, rect2) => {
  const x = Math.max(rect1.x, rect2.x);
  const y = Math.max(rect1.y, rect2.y);
  const largeur = Math.min(rect1.x + rect1.largeur, rect2.x + rect2.largeur) - x;
  const hauteur = Math.min(rect1.y + rect1.hauteur, rect2.y + rect2.hauteur) - y;

  if (largeur <= 0 || hauteur <= 0) return null;

  return { x, y, largeur, hauteur };
};

// Vérifie si deux ouvertures se chevauchent
export const ouverturesSeChevaucent = (ouverture1, ouverture2) => {
  return rectanglesSeChevaucent(ouverture1, ouverture2);
};

// Vérifie si une ouverture dépasse des limites du mur
export const ouvertureDebordeMur = (ouverture, mur) => {
  return (
    ouverture.x < 0 ||
    ouverture.y < 0 ||
    ouverture.x + ouverture.largeur > mur.largeur ||
    ouverture.y + ouverture.hauteur > mur.hauteur
  );
};

// --- Gestion des Chutes ---

const calculerChutesDeCoupe = (plaque, largeurOrigine, hauteurOrigine) => {
  const chutes = [];

  const diffLargeur = largeurOrigine - plaque.largeur;
  const diffHauteur = hauteurOrigine - plaque.hauteur;

  // On ignore les très petites chutes
  if (diffLargeur < 0.1 && diffHauteur < 0.1) return [];

  // Cas où la pièce utilisée est plus petite que l'origine dans les deux dimensions
  if (diffLargeur > 0 && diffHauteur > 0) {
    if (diffLargeur * hauteurOrigine > diffHauteur * largeurOrigine) {
      // Maximiser la chute verticale à droite
      chutes.push({
        largeur: diffLargeur,
        hauteur: hauteurOrigine,
        surface: diffLargeur * hauteurOrigine
      });
      chutes.push({
        largeur: plaque.largeur,
        hauteur: diffHauteur,
        surface: plaque.largeur * diffHauteur
      });
    } else {
      // Maximiser la chute horizontale en haut
      chutes.push({
        largeur: largeurOrigine,
        hauteur: diffHauteur,
        surface: largeurOrigine * diffHauteur
      });
      chutes.push({
        largeur: diffLargeur,
        hauteur: plaque.hauteur,
        surface: diffLargeur * plaque.hauteur
      });
    }
  } else if (diffLargeur > 0) {
    chutes.push({
      largeur: diffLargeur,
      hauteur: hauteurOrigine,
      surface: diffLargeur * hauteurOrigine
    });
  } else if (diffHauteur > 0) {
    chutes.push({
      largeur: largeurOrigine,
      hauteur: diffHauteur,
      surface: largeurOrigine * diffHauteur
    });
  }

  return chutes.filter(c => c.surface >= SURFACE_MINIMALE_REUTILISABLE);
};

// Cherche une chute adaptée dans le stock (Best Fit)
const trouverChuteIdeale = (largeurRequise, hauteurRequise, stockChutes) => {
  let meilleurIndex = -1;
  let minPerte = Infinity;
  let meilleurTournee = false;

  const surfaceNecessaire = largeurRequise * hauteurRequise;

  for (let i = 0; i < stockChutes.length; i++) {
    const chute = stockChutes[i];

    // Vérifier si la chute peut contenir la pièce dans un sens ou dans l'autre
    const rentreNormal = chute.largeur >= largeurRequise - 0.1 && chute.hauteur >= hauteurRequise - 0.1;
    const rentreTournee = chute.largeur >= hauteurRequise - 0.1 && chute.hauteur >= largeurRequise - 0.1;

    if (rentreNormal || rentreTournee) {
      const perte = chute.surface - surfaceNecessaire;

      if (perte < minPerte) {
        minPerte = perte;
        meilleurIndex = i;
        // Si elle ne rentre qu'en tournant, on marque la rotation
        meilleurTournee = !rentreNormal && rentreTournee;
      }
    }
  }

  if (meilleurIndex !== -1) {
    const chuteUtilisee = stockChutes[meilleurIndex];
    // Retirer la chute du stock
    stockChutes.splice(meilleurIndex, 1);

    // Si la chute n'est exploitable qu'en la tournant de 90°, on renvoie une
    // copie avec largeur/hauteur inversées pour que le calcul de pose et des
    // chutes résiduelles se fasse dans le bon référentiel.
    if (meilleurTournee) {
      return { ...chuteUtilisee, largeur: chuteUtilisee.hauteur, hauteur: chuteUtilisee.largeur };
    }
    return chuteUtilisee;
  }

  return null;
};

// --- Cœur de l'Optimisation ---

// Helper pour vérifier si une zone rectangulaire est entièrement vide (ie. couvert par une ouverture)
const couvertPhysique = (rect, ouvertures) => {
  return ouvertures.some(o => rectangleContient(o, rect));
};

const calculerDecoupes = (plaque, ouvertures) => {
  const dec = [];
  ouvertures.forEach(o => {
    if (rectanglesSeChevaucent(plaque, o)) {
      const inter = calculerIntersection(plaque, o);
      if (inter) {
        // Coordonnées relatives pour l'affichage interne à la plaque
        inter.xLocal = inter.x - plaque.x;
        inter.yLocal = inter.y - plaque.y;
        inter.type = o.type;
        dec.push(inter);
      }
    }
  });
  return dec;
};

// Calcule la disposition pour un seul mur
const optimiserPlacementMur = (mur, dimensionsPlaque, ouvertures, stockChutes, options = {}) => {
  const { largeur: murLargeur, hauteur: murHauteur } = mur;
  const { largeur: plaqueLargeur, hauteur: plaqueHauteur } = dimensionsPlaque;
  const decaleJoints = options.decaleJoints !== false; // Par défaut true
  const orientationNom = options.orientationNom || 'normal';

  let plaques = [];
  let chutesGenerees = [];

  // Dimensions standards de pose
  const lP = plaqueLargeur;
  const hP = plaqueHauteur;

  const nbLignes = Math.ceil(murHauteur / hP);

  for (let ligne = 0; ligne < nbLignes; ligne++) {
    const yPos = ligne * hP;
    // Hauteur visible sur le mur de cette ligne
    const hauteurLigneReelle = Math.min(hP, murHauteur - yPos);

    // Gestion du décalage (quinconce)
    // Une ligne sur deux, on décale
    let decalageX = 0;
    if (decaleJoints && ligne % 2 === 1) {
      decalageX = -(lP / 2); // On commence une demi-plaque avant le 0
    }

    let xCourant = decalageX;

    // Tant qu'on n'a pas couvert toute la largeur du mur
    while (xCourant < murLargeur) {
      // La section potentielle de plaque va de xCourant à xCourant + lP

      // Coordonnées RÉELLES sur le mur (intersection avec [0, murLargeur])
      let xDebutReel = Math.max(0, xCourant);
      let xFinReel = Math.min(murLargeur, xCourant + lP);

      let largeurVisible = xFinReel - xDebutReel;

      // Si hors limite
      if (xFinReel <= 0) {
        xCourant += lP;
        continue;
      }

      // --- Stratégie d'approvisionnement ---
      let source = 'neuve';
      let chuteUtilisee = trouverChuteIdeale(largeurVisible, hauteurLigneReelle, stockChutes);
      let dimensionsOriginales = { largeur: lP, hauteur: hP };

      if (chuteUtilisee) {
        source = 'chute';
        dimensionsOriginales = { largeur: chuteUtilisee.largeur, hauteur: chuteUtilisee.hauteur };
      }

      // Création de l'objet plaque pour le rendu
      const rectPlaque = {
        x: xDebutReel,
        y: yPos,
        largeur: largeurVisible,
        hauteur: hauteurLigneReelle
      };

      const estInutile = couvertPhysique(rectPlaque, ouvertures);

      if (!estInutile) {
        // Helper pour ajustement nécessaire (tolérance float)
        const ajustementNecessaire = (plaqueWidth, plaqueHeight, originalWidth, originalHeight) => {
          return plaqueWidth < originalWidth - 0.1 || plaqueHeight < originalHeight - 0.1;
        };

        const plaque = {
          x: rectPlaque.x,
          y: rectPlaque.y,
          largeur: rectPlaque.largeur,
          hauteur: rectPlaque.hauteur,
          orientation: orientationNom,
          source: source,
          // Pour l'UI existante qui s'attend à ce champ
          ajustementNecessaire: ajustementNecessaire(rectPlaque.largeur, rectPlaque.hauteur, dimensionsOriginales.largeur, dimensionsOriginales.hauteur),
          chuteId: chuteUtilisee ? chuteUtilisee.id : null,
          decoupes: [],
          chutesGenereesFromThis: []
        };

        // Calcul des découpes internes (fenêtres/portes)
        plaque.decoupes = calculerDecoupes(plaque, ouvertures);

        // Calcul des chutes générées par cette opération (Offcuts)
        const chutesDeCetteOperation = calculerChutesDeCoupe(
          { largeur: plaque.largeur, hauteur: plaque.hauteur },
          dimensionsOriginales.largeur,
          dimensionsOriginales.hauteur
        );

        // On marque les chutes avec des IDs et l'origine
        chutesDeCetteOperation.forEach(c => {
          c.id = Math.random().toString(36).substr(2, 9);
          c.sourceMurId = mur.id;
        });

        plaque.chutesGenereesFromThis = chutesDeCetteOperation;

        // Stocker les chutes
        stockChutes.push(...chutesDeCetteOperation);
        chutesGenerees.push(...chutesDeCetteOperation);

        plaques.push(plaque);
      }

      xCourant += lP;
    }
  }

  return { plaques, chutesGenerees };
};


// --- Point d'entrée principal ---

export const optimiserTousMurs = (murs, dimensionsPlaque, utiliserChutes = true) => {
  // Reset initial
  let stockChutes = [];
  let toutesLesPlaques = [];
  let resultatsParMur = [];

  // Statistiques Globales
  let surfaceUtileTotale = 0;
  let surfacePlaquesNeuvesUtilisees = 0;
  let nbPlaquesNeuves = 0;
  let nbChutesReutilisees = 0;
  let surfaceChutesReutilisees = 0;

  murs.forEach(mur => {
    // Cloner les ouvertures
    const ouvertures = mur.ouvertures.map(o => ({ ...o }));

    // -- Optimisation Double-Pass (Verticale vs Horizontale) --

    const stockEntree = utiliserChutes ? stockChutes : [];

    // 1. Simulation Normale (ex: Verticale)
    // On utilise une copie du stock pour ne pas impacter la simu 2
    const stockSimu1 = [...stockEntree];
    const res1 = optimiserPlacementMur(mur, dimensionsPlaque, ouvertures, stockSimu1, { decaleJoints: true, orientationNom: 'normal' });
    const nbNeuves1 = res1.plaques.filter(p => p.source === 'neuve').length;

    // 2. Simulation Rotation (ex: Horizontale)
    const stockSimu2 = [...stockEntree];
    const dimensionsRotate = { largeur: dimensionsPlaque.hauteur, hauteur: dimensionsPlaque.largeur };
    const res2 = optimiserPlacementMur(mur, dimensionsRotate, ouvertures, stockSimu2, { decaleJoints: true, orientationNom: 'rotated' });
    const nbNeuves2 = res2.plaques.filter(p => p.source === 'neuve').length;

    // Choix du vainqueur (Moins de plaques neuves = Mieux)
    const nbAjustements1 = res1.plaques.filter(p => p.ajustementNecessaire).length;
    const nbAjustements2 = res2.plaques.filter(p => p.ajustementNecessaire).length;

    let winnerRes, winnerStock, winnerDim, winnerOrient;

    let chooseRotated = false;

    if (nbNeuves2 < nbNeuves1) {
      chooseRotated = true;
    } else if (nbNeuves2 === nbNeuves1) {
      if (nbAjustements2 < nbAjustements1) {
        chooseRotated = true;
      } else if (nbAjustements2 === nbAjustements1) {
        // En cas d'égalité stricte (mêmes plaques, mêmes coupes), on privilégie Horizontale
        chooseRotated = true;
      }
    }

    if (chooseRotated) {
      winnerRes = res2;
      winnerStock = stockSimu2;
      winnerDim = dimensionsRotate;
      winnerOrient = 'rotated';
    } else {
      winnerRes = res1;
      winnerStock = stockSimu1;
      winnerDim = dimensionsPlaque;
      winnerOrient = 'normal';
    }

    // Si on utilise le stock global, on le met à jour avec le stock résultant du vainqueur
    if (utiliserChutes) {
      // Attention : stockChutes doit être remplacé par le nouveau tableau
      stockChutes = winnerStock;
    }

    const plaquesMur = winnerRes.plaques.map(p => ({
      ...p,
      murId: mur.id,
      murNom: mur.nom,
      orientation: winnerOrient
    }));

    toutesLesPlaques.push(...plaquesMur);

    // Statistiques du Mur
    const surfaceMur = mur.largeur * mur.hauteur;
    const surfaceOuv = ouvertures.reduce((acc, o) => acc + (o.largeur * o.hauteur), 0);
    const surfaceUtile = surfaceMur - surfaceOuv;
    surfaceUtileTotale += surfaceUtile;

    const nbPlaquesNeuvesMur = plaquesMur.filter(p => p.source === 'neuve').length;
    nbPlaquesNeuves += nbPlaquesNeuvesMur;

    // Chutes réellement réemployées sur ce mur (morceaux issus du stock de chutes)
    const plaquesChuteMur = plaquesMur.filter(p => p.source === 'chute');
    const nbChutesUtiliseesMur = plaquesChuteMur.length;
    const surfaceChutesUtiliseesMur = plaquesChuteMur.reduce((acc, p) => acc + (p.largeur * p.hauteur), 0);
    nbChutesReutilisees += nbChutesUtiliseesMur;
    surfaceChutesReutilisees += surfaceChutesUtiliseesMur;

    surfacePlaquesNeuvesUtilisees += nbPlaquesNeuvesMur * (dimensionsPlaque.largeur * dimensionsPlaque.hauteur);

    resultatsParMur.push({
      murId: mur.id,
      murNom: mur.nom,
      nbPlaques: plaquesMur.length,
      nbPlaquesNeuves: nbPlaquesNeuvesMur,
      nbChutesUtilisees: nbChutesUtiliseesMur,
      surfaceChutesUtilisees: surfaceChutesUtiliseesMur,
      orientationChoisie: winnerOrient,
      surfaceUtile,
      dimensions: `${mur.largeur} x ${mur.hauteur}`,
      nbOuvertures: ouvertures.length,
      chutesGenerees: winnerRes.chutesGenerees.length
    });
  });

  // Calcul final des chutes RESTANTES
  const chutesRestantes = stockChutes;
  const surfaceChutesRestantes = chutesRestantes.reduce((acc, c) => acc + c.surface, 0);

  const pourcentageChutes = nbPlaquesNeuves > 0
    ? ((surfacePlaquesNeuvesUtilisees - surfaceUtileTotale) / surfacePlaquesNeuvesUtilisees) * 100
    : 0;

  return {
    plaques: toutesLesPlaques,
    nbPlaques: nbPlaquesNeuves,
    nbMorceauxTotal: toutesLesPlaques.length,
    surfaceTotale: surfacePlaquesNeuvesUtilisees,
    surfaceUtile: surfaceUtileTotale,
    pourcentageChutes: Math.max(0, pourcentageChutes),
    murDetails: resultatsParMur,
    // Bilan de la réutilisation des chutes, consommé par ResultatsGlobaux et CostCalculator
    economieChutes: {
      nbChutesUtilisees: nbChutesReutilisees,
      surfaceChutesUtilisees: surfaceChutesReutilisees,
      nbChutesDisponibles: chutesRestantes.length,
      surfaceChutesDisponibles: surfaceChutesRestantes,
      // Part de la surface posée qui provient de chutes réemployées (vs plaques neuves)
      pourcentageEconomie: surfaceUtileTotale > 0
        ? Math.min(100, (surfaceChutesReutilisees / surfaceUtileTotale) * 100)
        : 0
    },
    chutesReutilisables: {
      nombre: chutesRestantes.length,
      surface: surfaceChutesRestantes,
      surfaceEnM2: surfaceChutesRestantes / 10000,
      details: chutesRestantes
    }
  };
};